'use client';

/**
 * PropertyCard - Core Reusable Property Card Component
 *
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Uses Tailwind CSS for styling - themes can override via className prop.
 *
 * Now uses FormatPrice for currency-aware price display with automatic conversion.
 * Includes WishlistButton for saving properties to wishlists.
 */

import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';
import { WishlistButton } from '@/components/customer/WishlistButton';
import type { Property } from '@/lib/api/clients/properties';
import Image from 'next/image';
import Link from 'next/link';

export interface PropertyCardProps {
  property: Property;
  href?: string;
  variant?: 'default' | 'compact' | 'featured';
  className?: string;
  /** Whether to show original price alongside converted price */
  showOriginalPrice?: boolean;
  /** Whether to show the wishlist (heart) button */
  showWishlistButton?: boolean;
}

/**
 * Core Property Card Component
 *
 * Provides the semantic structure and data display logic.
 * Themes can customize appearance via className overrides.
 */
export function PropertyCard({
  property,
  href,
  variant = 'default',
  className = '',
  showOriginalPrice = false,
  showWishlistButton = true,
}: PropertyCardProps) {
  const cardClasses = `bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 ${
    variant === 'compact' ? 'flex flex-row' : ''
  } ${variant === 'featured' ? 'ring-2 ring-primary' : ''} ${className}`;

  const { amount: price, currency } = extractBestPriceSimple(property);

  const cardContent = (
    <div className={cardClasses}>
      {/* Image */}
      <div
        className={`relative overflow-hidden bg-gray-100 ${
          variant === 'compact' ? 'w-48 flex-shrink-0' : 'w-full aspect-[4/3]'
        }`}
      >
        {property.heroMedia?.url || property.primaryImageUrl ? (
          <Image
            src={property.heroMedia?.url || property.primaryImageUrl || ''}
            alt={property.name}
            width={variant === 'compact' ? 200 : 400}
            height={variant === 'compact' ? 150 : 300}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
            <span>No Image</span>
          </div>
        )}

        {/* Wishlist Button */}
        {showWishlistButton && (
          <div className="absolute top-2 left-2 z-10">
            <WishlistButton
              entityType="PROPERTY"
              entityId={property.id}
              variant="solid"
              size="sm"
            />
          </div>
        )}

        {/* Status Badge */}
        {property.status && property.status !== 'ACTIVE' && (
          <div
            className={`absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded ${
              property.status === 'DRAFT'
                ? 'bg-gray-500 text-white'
                : (property.status === 'INACTIVE' || property.status === 'ARCHIVED')
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-500 text-white'
            }`}
          >
            {(property.status === 'ARCHIVED' || property.status === 'INACTIVE' || (property as unknown as Record<string, unknown>).listingStatus === 'SOLD')
              ? 'Sold'
              : property.status}
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`p-4 flex flex-col gap-2 ${variant === 'compact' ? 'flex-1' : ''}`}
      >
        {/* Title */}
        <h2 className="text-lg font-semibold text-gray-900 line-clamp-2">
          {property.name}
        </h2>

        {/* Price - Now uses FormatPrice with currency conversion */}
        <div className="text-xl font-bold text-primary">
          <FormatPrice
            amount={price}
            currency={currency}
            size="lg"
            showOriginal={showOriginalPrice}
          />
        </div>

        {/* Key Details */}
        <div className="flex flex-wrap gap-3 text-sm text-gray-600">
          {property.bedrooms && (
            <div className="flex items-center gap-1">
              <span className="text-base">🛏️</span>
              <span>
                {property.bedrooms} {property.bedrooms === 1 ? 'bed' : 'beds'}
              </span>
            </div>
          )}

          {property.bathrooms && (
            <div className="flex items-center gap-1">
              <span className="text-base">🚿</span>
              <span>
                {property.bathrooms}{' '}
                {property.bathrooms === 1 ? 'bath' : 'baths'}
              </span>
            </div>
          )}

          {property.totalFloorArea && (
            <div className="flex items-center gap-1">
              <span className="text-base">📐</span>
              <span>
                {property.totalFloorArea}{' '}
                {property.totalFloorAreaUnit?.toLowerCase()}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {property.description && variant !== 'compact' && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {truncateText(property.description, 100)}
          </p>
        )}

        {/* Action */}
        {variant !== 'compact' && (
          <div className="flex items-center justify-between pt-2 mt-auto border-t border-gray-200 text-sm font-medium text-primary">
            <span>View Details</span>
            <span className="transition-transform hover:translate-x-1">→</span>
          </div>
        )}
      </div>
    </div>
  );

  // Wrap in Link if href provided
  if (href) {
    return (
      <Link href={href} className="block no-underline text-inherit">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

/**
 * Truncate text to specified length
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}
